import { Building2, Handshake, Users, ShieldCheck, HeartHandshake, Eye } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { useSiteSettings } from "@/lib/settings";

const PILLARS = [
  { icon: ShieldCheck, title: "Professionalism", desc: "Rigorous, well-defined recruitment process led by pharma-native domain experts." },
  { icon: HeartHandshake, title: "Integrity", desc: "The bedrock of every mandate — confidential, ethical and candidate-friendly by default." },
  { icon: Eye, title: "Transparency", desc: "Clear communication, honest feedback and no hidden fees — from brief to offer." },
];

/**
 * Visual: Organisational Growth ↔ Pharmaco Hires ↔ Exceptional Talent  with 3 pillars below.
 * Previously lived on the Home "Bridging the Complexity" section — now used on About / Our Story.
 */
export default function BridgeVisual({ className = "" }) {
  const { settings } = useSiteSettings();
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8 ${className}`} data-testid="bridge-visual">
      <div className="grid grid-cols-3 items-center gap-4 sm:gap-6">
        <Reveal variant="left" className="text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-6 w-6" /></div>
          <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Organisational</div>
          <div className="font-heading text-sm font-bold text-slate-900">Growth</div>
        </Reveal>
        <div className="flex flex-col items-center">
          <Handshake className="h-10 w-10 text-secondary" />
          <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-secondary">
            {settings?.brand_left || "Pharmaco"} {settings?.brand_right || "Hires"}
          </div>
        </div>
        <Reveal variant="right" className="text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-xl bg-secondary/15 text-secondary"><Users className="h-6 w-6" /></div>
          <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Exceptional</div>
          <div className="font-heading text-sm font-bold text-slate-900">Talent</div>
        </Reveal>
      </div>
      <svg className="mx-auto mt-6 block h-[8px] w-full max-w-xl" viewBox="0 0 500 8" preserveAspectRatio="none" aria-hidden="true">
        <line x1="0" y1="4" x2="500" y2="4" stroke="hsl(var(--primary))" strokeOpacity="0.25" strokeWidth="2" />
        <line x1="0" y1="4" x2="500" y2="4" stroke="hsl(var(--secondary))" strokeWidth="2" strokeLinecap="round" className="connector-flow" />
      </svg>
      <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="bridge-pillars">
        {PILLARS.map(({ icon: Icon, title, desc }, i) => (
          <Reveal
            key={title}
            variant="up"
            delay={i * 90}
            data-testid={`bridge-pillar-${title.toLowerCase()}`}
            className="rounded-xl border border-slate-200 bg-white p-4 hover-lift hover:border-primary/40"
          >
            <div className="mb-2 inline-grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
            <div className="font-heading text-sm font-semibold text-slate-900">{title}</div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
